import {
  HistogramBucket,
  DistributionApiResponse,
  DistributionData,
  HistogramAggregation,
  FilteredHistogramAggregation,
  StatsAggregation,
  FilteredStatsAggregation,
  PriceMode,
} from './DistributionSection.types'

import {
  processRoomsBuckets,
  processPriceBuckets,
  processPriceM2Buckets,
} from './distributionBuckets'
import { processAreaBuckets } from './distributionArea'
import {
  processConstructionYearBuckets,
  processListingDurationBuckets,
} from './distributionTimeBuckets'

export {
  convertRentalPrice,
  formatPrice,
  formatArea,
  formatRooms,
} from './distributionFormatting'
export {
  processRoomsBuckets,
  processPriceBuckets,
  processPriceM2Buckets,
} from './distributionBuckets'
export { processAreaBuckets } from './distributionArea'
export {
  processConstructionYearBuckets,
  processListingDurationBuckets,
} from './distributionTimeBuckets'

/**
 * Helper to extract buckets from either a direct histogram or a filtered histogram response
 */
const extractHistogramBuckets = (
  agg: HistogramAggregation | FilteredHistogramAggregation | undefined
): HistogramBucket[] => {
  if (!agg) return []
  // Check if it's a filtered aggregation (has 'histogram' property with buckets)
  if ('histogram' in agg && agg.histogram?.buckets) {
    return agg.histogram.buckets
  }
  // Otherwise it's a direct histogram
  if ('buckets' in agg) {
    return agg.buckets
  }
  return []
}

/**
 * Helper to extract stats from either direct stats or filtered stats response
 */
const extractStats = (
  agg: StatsAggregation | FilteredStatsAggregation | undefined
): StatsAggregation | undefined => {
  if (!agg) return undefined
  // Check if it's a filtered aggregation (has 'stats' property)
  if ('stats' in agg && agg.stats) {
    return agg.stats
  }
  // Otherwise it's direct stats (has 'avg' property)
  if ('avg' in agg) {
    return agg as StatsAggregation
  }
  return undefined
}

/**
 * Calculate median from histogram buckets (approximation)
 */
export const calculateMedianFromBuckets = (
  buckets: HistogramBucket[],
  interval: number
): number => {
  const total = buckets.reduce((sum, b) => sum + b.doc_count, 0)
  if (total === 0) return 0

  const halfTotal = total / 2
  let cumulative = 0

  for (const bucket of buckets) {
    cumulative += bucket.doc_count
    if (cumulative >= halfTotal) {
      // Linear interpolation within the bucket
      const prevCumulative = cumulative - bucket.doc_count
      const fraction = (halfTotal - prevCumulative) / bucket.doc_count
      return bucket.key + fraction * interval
    }
  }

  return buckets[buckets.length - 1]?.key || 0
}

/**
 * Process API response into DistributionData
 */
export const processDistributionData = (
  response: DistributionApiResponse,
  isRental: boolean,
  priceMode?: PriceMode
): DistributionData => {
  const rooms = processRoomsBuckets(response.rooms_histogram?.buckets || [])
  const price = processPriceBuckets(
    response.price_histogram?.buckets || [],
    isRental,
    priceMode
  )
  const priceM2 = processPriceM2Buckets(
    response.price_m2_histogram?.buckets || [],
    isRental,
    priceMode
  )
  const areaBuckets = extractHistogramBuckets(response.area_histogram).filter(
    (bucket) => bucket.key >= 0
  )
  const area = processAreaBuckets(areaBuckets)
  const areaStats = extractStats(response.area_stats)
  const constructionYear = processConstructionYearBuckets(
    extractHistogramBuckets(response.construction_year_histogram)
  )
  const listingDuration = processListingDurationBuckets(
    response.listing_duration_histogram?.buckets || []
  )

  // Calculate totals from data
  const roomsTotal = rooms.reduce((sum, r) => sum + r.count, 0)
  const priceTotal = price.reduce((sum, p) => sum + p.count, 0)
  const priceM2Total = priceM2.reduce((sum, p) => sum + p.count, 0)
  const areaTotal = area.reduce((sum, a) => sum + a.count, 0)
  const constructionYearTotal = constructionYear.reduce(
    (sum, c) => sum + c.count,
    0
  )
  const listingDurationTotal = listingDuration.reduce(
    (sum, l) => sum + l.count,
    0
  )

  return {
    rooms,
    price,
    priceM2,
    area,
    constructionYear,
    listingDuration,
    stats: {
      rooms: {
        median: calculateMedianFromBuckets(
          response.rooms_histogram?.buckets || [],
          0.5
        ),
        avg: response.rooms_stats?.avg || 0,
        min: response.rooms_stats?.min || 0,
        max: response.rooms_stats?.max || 0,
        total: roomsTotal,
      },
      price: {
        median: calculateMedianFromBuckets(
          response.price_histogram?.buckets || [],
          isRental ? 500 : 200000
        ),
        avg: response.price_stats?.avg || 0,
        min: response.price_stats?.min || 0,
        max: response.price_stats?.max || 0,
        total: priceTotal,
      },
      priceM2: {
        median: calculateMedianFromBuckets(
          response.price_m2_histogram?.buckets || [],
          isRental ? 10 : 2000
        ),
        avg: response.price_m2_stats?.avg || 0,
        min: response.price_m2_stats?.min || 0,
        max: response.price_m2_stats?.max || 0,
        total: priceM2Total,
      },
      area: {
        median: calculateMedianFromBuckets(areaBuckets, 25),
        avg: areaStats?.avg || 0,
        min: areaStats?.min || 0,
        max: areaStats?.max || 0,
        total: areaTotal,
      },
      constructionYear: {
        median: calculateMedianFromBuckets(
          extractHistogramBuckets(response.construction_year_histogram),
          10
        ),
        avg: extractStats(response.construction_year_stats)?.avg || 0,
        min: extractStats(response.construction_year_stats)?.min || 0,
        max: extractStats(response.construction_year_stats)?.max || 0,
        total: constructionYearTotal,
      },
      listingDuration: {
        median: calculateMedianFromBuckets(
          response.listing_duration_histogram?.buckets || [],
          30
        ),
        avg: response.listing_duration_stats?.avg || 0,
        min: response.listing_duration_stats?.min || 0,
        max: response.listing_duration_stats?.max || 0,
        total: listingDurationTotal,
      },
    },
  }
}
